import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { Loader2 } from 'lucide-react';
import { ROUTE_PATHS } from '@/config/routes.config';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: string;
}

function waitForAuthHydration(): Promise<void> {
  const persistApi = useAuthStore.persist;
  if (persistApi.hasHydrated()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsub = persistApi.onFinishHydration(() => {
      unsub();
      resolve();
    });
  });
}

export function AuthGuard({ children, requiredRole }: AuthGuardProps) {
  const { isAuthenticated, user, isLoading, checkAuth } = useAuthStore();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        // Wait for minimal persisted state, then load full profile (email, role, etc.)
        await waitForAuthHydration();
        if (cancelled) return;
        await checkAuth();
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, [checkAuth]);

  useEffect(() => {
    if (!isChecking && !isLoading) {
      if (!isAuthenticated) {
        navigate(ROUTE_PATHS.LOGIN, { replace: true });
        return;
      }

      // Check role-based access
      if (requiredRole && user?.role !== requiredRole) {
        navigate(ROUTE_PATHS.UNAUTHORIZED, { replace: true });
        return;
      }
    }
  }, [isAuthenticated, user, requiredRole, isChecking, isLoading, navigate]);

  // Show loading while checking authentication
  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Check role-based access
  if (requiredRole && user?.role !== requiredRole) {
    return null;
  }

  return <>{children}</>;
}
